import { downloadFileWithProgress, getFetcher, postFetcher } from "api/swr";
import { cdnUrl } from "commons/cdn";
import JSZip from "jszip";
import { validateSpineJson, parseSpineToConfig, parseSkins, ParsedSkin } from "../../lib/spineParser";
import {
  orderSelectionsFromDb,
  sortSelectionsByPlayIndex,
} from "../../lib/selectionUtils";

export interface ProcessedTemplateData {
  spineData: any;
  templateConfig: any;
  parsedSkins: ParsedSkin[];
}

export async function processTemplate(
  tid: string,
  template: any,
  onProgress: (msg: string) => void
): Promise<ProcessedTemplateData> {
  // 如果没有 ZIP URL，说明是空模板
  if (!template.spineZipUrl) {
    throw new Error("模板缺少 ZIP 文件 URL");
  }

  // 下载 ZIP（与图片一致：zthd.io → yintuhc.cn，避免 CDN 缺 CORS）
  onProgress("正在下载 ZIP 文件...");
  const zipBlob = await downloadFileWithProgress(cdnUrl(template.spineZipUrl), {
    timeout: 90000,
    onProgress: (loaded, total, percent) => {
      if (total > 0) {
        onProgress(`正在下载... ${percent.toFixed(1)}%`);
      }
    },
  });

  // 解压
  onProgress("正在解压...");
  const zip = new JSZip();
  const zipData = await zip.loadAsync(zipBlob);

  // Zip 内骨骼文件名可与当前 tid 不一致（子模版共用父 Zip 时常见）
  const atlasFiles: { name: string; file: any }[] = [];
  const pngFiles: { name: string; file: any }[] = [];
  const jsonCandidates: { name: string; file: any }[] = [];
  let skelFile: any = null;

  zipData.forEach((relativePath, file) => {
    if (file.dir) return;
    const lower = relativePath.toLowerCase();
    if (lower.endsWith(".atlas")) {
      atlasFiles.push({ name: relativePath, file });
    } else if (lower.endsWith(".png")) {
      pngFiles.push({ name: relativePath, file });
    } else if (lower.endsWith(".skel")) {
      skelFile = file;
    } else if (lower.endsWith(".json")) {
      jsonCandidates.push({ name: relativePath, file });
    }
  });

  const jsonFile =
    zipData.file(`${tid}.json`) ||
    jsonCandidates.find((f) => f.name === `${tid}.json`)?.file ||
    jsonCandidates[0]?.file ||
    null;

  // 必须有 json 或 skel 之一
  if (!jsonFile && !skelFile) {
    throw new Error("ZIP 文件中缺少 .json 或 .skel 文件");
  }
  if (atlasFiles.length === 0 || pngFiles.length === 0) {
    throw new Error("ZIP 文件中缺少 .atlas 或 .png 文件");
  }

  // 读取文件
  onProgress("正在解析数据...");

  let jsonData: any;
  let jsonText: string = "";
  let skelBytes: Uint8Array | undefined;

  if (skelFile) {
    // .skel 二进制模式 — 需要真实 atlas 才能解析，此处用占位数据
    const skelArrayBuffer = await skelFile.async("arraybuffer");
    skelBytes = new Uint8Array(skelArrayBuffer);
    jsonData = {
      skeleton: {},
      skins: [],
      animations: {},
      slots: [],
      _fromSkel: true,
      _pendingSkelParse: true,
    };
  } else {
    jsonText = await jsonFile!.async("string");
    jsonData = JSON.parse(jsonText);
    if (!validateSpineJson(jsonData)) {
      throw new Error("无效的 Spine JSON 格式");
    }
  }

  // 读取所有 Atlas 文件（支持多个）
  const atlasTexts: Record<string, string> = {};
  await Promise.all(
    atlasFiles.map(async ({ name, file }) => {
      const atlasText = await file.async("string");
      atlasTexts[name] = atlasText;
    })
  );

  const pngDataUrls: Record<string, string> = {};
  await Promise.all(
    pngFiles.map(async ({ name, file }) => {
      const pngBlob = await file.async("blob");
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error(`读取失败: ${name}`));
        reader.readAsDataURL(pngBlob);
      });
      pngDataUrls[name] = dataUrl;
    })
  );

  const jsonData_parsed = jsonData;

  const spineData: any = {
    json: jsonData_parsed,
    jsonText,
    atlasTexts,
    pngDataUrls,
  };
  if (skelBytes) {
    spineData.skelBytes = skelBytes;
  }

  const parsedSkins = parseSkins(jsonData_parsed);

  // 加载 SKU 配置
  onProgress("正在加载配置...");
  let skus: Record<string, any> = {};
  try {
    const spineSkus = await getFetcher(`/spine-templates/${tid}/skus`);
    if (Array.isArray(spineSkus)) {
      for (const sku of spineSkus) {
        const { id: skuId, tid: _, updateTime: __, ...skuData } = sku;
        // 存储完整的 SKU 对象
        skus[skuId] = {
          id: skuId,
          skinName: skuData.skinName,
          category: skuData.category,
          pro: skuData.pro,
          toningIds: skuData.toningIds,
          hidden: skuData.hidden,
          originalAssetId: skuData.originalAssetId,
          thumbAssetId: skuData.thumbAssetId,
        };
      }
    }
  } catch (e) {
    console.warn("加载 spineSkus 失败", e);
  }

  // 还原 selection（只存 skuIds）
  const baseConfig = parseSpineToConfig(jsonData_parsed);
  let selectionData = baseConfig.selection;

  // 优先从 selectionIds 读取（新格式）
  if (template.selectionIds) {
    try {
      const selectionIds = JSON.parse(template.selectionIds);
      if (Array.isArray(selectionIds) && selectionIds.length > 0) {
        onProgress("正在加载 selections...");
        const selectionsFromDb = await postFetcher(
          "/spine-selections/get-by-ids",
          {
            arg: { ids: selectionIds },
          }
        );
        if (Array.isArray(selectionsFromDb) && selectionsFromDb.length > 0) {
          selectionData = orderSelectionsFromDb(
            selectionIds,
            selectionsFromDb,
          );
        }
      }
    } catch (e) {
      console.warn("从 selectionIds 加载失败，尝试使用 selections 字段:", e);
      // 如果从 selectionIds 加载失败，fallback 到 selections
      if (template.selections) {
        const savedSelections = JSON.parse(template.selections);
        selectionData = sortSelectionsByPlayIndex(
          savedSelections.map((sel: any) => ({
            id: sel.id,
            playIndex: sel.playIndex,
            layerIconId: sel.layerIconId,
            gid: sel.gid,
            background: sel.background,
            name: sel.name,
            skuIds: sel.skuIds || [],
            hidden: sel.hidden || undefined,
            opt: typeof sel.opt === "boolean" ? sel.opt : undefined,
          })),
        );
      }
    }
  } else if (template.selections) {
    // 如果没有 selectionIds，使用旧的 selections 字段
    const savedSelections = JSON.parse(template.selections);
    selectionData = sortSelectionsByPlayIndex(
      savedSelections.map((sel: any) => ({
        id: sel.id,
        playIndex: sel.playIndex,
        layerIconId: sel.layerIconId,
        gid: sel.gid,
        background: sel.background,
        name: sel.name,
        skuIds: sel.skuIds || [],
        hidden: sel.hidden || undefined,
        opt: typeof sel.opt === "boolean" ? sel.opt : undefined,
      })),
    );
  }

  // 加载 tonings 数据（新格式：ID 数组）
  let toningsData: any[] = [];
  if (template.tonings) {
    try {
      const parsedTonings = JSON.parse(template.tonings);
      if (Array.isArray(parsedTonings)) {
        // 检查是 ID 数组还是完整对象数组
        if (
          parsedTonings.length > 0 &&
          typeof parsedTonings[0] === "string"
        ) {
          // 新格式：ID 数组，需要从全局库获取完整数据
          onProgress("正在加载调色方案...");
          const toningIds = parsedTonings as string[];
          if (toningIds.length > 0) {
            toningsData = await postFetcher("/spine-tonings/batch", {
              arg: { ids: toningIds },
            });
          }
        } else {
          // 旧格式：完整对象数组，直接使用
          toningsData = parsedTonings;
        }
      }
    } catch (e) {
      console.warn("解析 tonings 失败:", e);
    }
  }

  const templateConfig = {
    ...baseConfig,
    tonings: toningsData.length > 0 ? toningsData : baseConfig.tonings,
    animations: template.animations
      ? JSON.parse(template.animations)
      : baseConfig.animations,
    requireCategories: template.requireCategories
      ? JSON.parse(template.requireCategories)
      : [],
    selection: selectionData,
    skus,
  };

  return {
    spineData,
    templateConfig,
    parsedSkins,
  };
}

/** bgStatic / fgStatic：无 Zip，仅 Firestore 中的 selections + spineSkus */
export async function processStaticLayerTemplate(
  tid: string,
  template: any,
  onProgress: (msg: string) => void,
): Promise<ProcessedTemplateData> {
  onProgress("正在加载配置...");

  const baseConfig = {
    selection: [] as any[],
    tonings: [] as any[],
    animations: [] as any[],
    requireCategories: [] as any[],
  };

  let skus: Record<string, any> = {};
  try {
    const spineSkus = await getFetcher(`/spine-templates/${tid}/skus`);
    if (Array.isArray(spineSkus)) {
      for (const sku of spineSkus) {
        const { id: skuId, tid: _t, updateTime: _u, ...skuData } = sku;
        skus[skuId] = {
          id: skuId,
          skinName: skuData.skinName,
          category: skuData.category,
          pro: skuData.pro,
          toningIds: skuData.toningIds,
          hidden: skuData.hidden,
          originalAssetId: skuData.originalAssetId,
          thumbAssetId: skuData.thumbAssetId,
        };
      }
    }
  } catch (e) {
    console.warn("加载 spineSkus 失败", e);
  }

  let selectionData = baseConfig.selection;

  if (template.selectionIds) {
    try {
      const selectionIds = JSON.parse(template.selectionIds);
      if (Array.isArray(selectionIds) && selectionIds.length > 0) {
        onProgress("正在加载 selections...");
        const selectionsFromDb = await postFetcher(
          "/spine-selections/get-by-ids",
          { arg: { ids: selectionIds } },
        );
        if (Array.isArray(selectionsFromDb) && selectionsFromDb.length > 0) {
          selectionData = orderSelectionsFromDb(
            selectionIds,
            selectionsFromDb,
          );
        }
      }
    } catch (e) {
      console.warn("从 selectionIds 加载失败:", e);
      if (template.selections) {
        const savedSelections = JSON.parse(template.selections);
        selectionData = sortSelectionsByPlayIndex(
          savedSelections.map((sel: any) => ({
            id: sel.id,
            playIndex: sel.playIndex,
            layerIconId: sel.layerIconId,
            gid: sel.gid,
            background: sel.background,
            name: sel.name,
            skuIds: sel.skuIds || [],
            hidden: sel.hidden || undefined,
            opt: typeof sel.opt === "boolean" ? sel.opt : undefined,
          })),
        );
      }
    }
  } else if (template.selections) {
    const savedSelections = JSON.parse(template.selections);
    selectionData = sortSelectionsByPlayIndex(
      savedSelections.map((sel: any) => ({
        id: sel.id,
        playIndex: sel.playIndex,
        layerIconId: sel.layerIconId,
        gid: sel.gid,
        background: sel.background,
        name: sel.name,
        skuIds: sel.skuIds || [],
        hidden: sel.hidden || undefined,
        opt: typeof sel.opt === "boolean" ? sel.opt : undefined,
      })),
    );
  }

  let toningsData: any[] = [];
  if (template.tonings) {
    try {
      const parsedTonings = JSON.parse(template.tonings);
      if (Array.isArray(parsedTonings)) {
        if (
          parsedTonings.length > 0 &&
          typeof parsedTonings[0] === "string"
        ) {
          onProgress("正在加载调色方案...");
          const toningIds = parsedTonings as string[];
          if (toningIds.length > 0) {
            toningsData = await postFetcher("/spine-tonings/batch", {
              arg: { ids: toningIds },
            });
          }
        } else {
          toningsData = parsedTonings;
        }
      }
    } catch (e) {
      console.warn("解析 tonings 失败:", e);
    }
  }

  let animations: any[] = [];
  if (template.animations) {
    try {
      animations = JSON.parse(template.animations);
    } catch {
      animations = [];
    }
  }

  const templateConfig = {
    ...baseConfig,
    tonings: toningsData.length > 0 ? toningsData : baseConfig.tonings,
    animations,
    requireCategories: template.requireCategories
      ? JSON.parse(template.requireCategories)
      : [],
    selection: selectionData,
    skus,
  };

  return {
    spineData: null,
    templateConfig,
    parsedSkins: [],
  };
}
